import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { subscriptionsApi } from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import { LANDING_LIB_IMAGES } from './landingMedia'
import { DEFAULT_LANDING_PLANS, mergeLandingPlans, type LandingPlanCard } from './landingPlans'
import { trackEvent } from '../../utils/productAnalytics'
import BrandLogo from '../../components/BrandLogo/BrandLogo'

import {
  applyLandingSeo,
  initLandingAnalytics,
  trackLandingCta,
  useLandingDarkThemeEffect,
} from './landingUtils'
import './LandingPage.css'
import '../../tokens/shared.css'

/** Six style cards for landing (conversion — not full catalog). */
const LANDING_STYLES = [
  {
    title: 'KRT Studio',
    blurb: 'Чистая карточка товара для маркетплейса',
    img: '/stile/krt-studio.png?v=2',
  },
  {
    title: 'KRT Premium',
    blurb: 'Статус и желание обладать',
    img: '/stile/krt-premium.png?v=1',
  },
  {
    title: 'KRT Lifestyle',
    blurb: 'Товар в жизни — тепло и доверие',
    img: '/stile/krt-lifestyle.png?v=1',
  },
  {
    title: 'KRT Campaign',
    blurb: 'Вау-кадр для баннеров и запусков',
    img: '/stile/krt-campaign.png?v=1',
  },
  {
    title: 'KRT Reality',
    blurb: 'Премиальное коммерческое фото',
    img: '/stile/krt-reality.png?v=2',
  },
  {
    title: 'KRT Graphic',
    blurb: 'Чистые формы и бренд-цвет',
    img: '/stile/krt-graphic.png?v=1',
  },
] as const

const scenarios = [
  {
    title: 'AI-изображения',
    desc: 'Главный инструмент KartinAi: стили, правки и серии кадров без дизайнера.',
    cta: 'Попробовать',
    source: 'scenario_images',
  },
  {
    title: 'Реклама и соцсети',
    desc: 'Стили Premium, Lifestyle и Campaign — от статуса до вау-баннера под ваш бриф.',
    cta: 'Собрать креатив',
    source: 'scenario_ads',
  },
  {
    title: 'Видео',
    desc: 'Video Studio: из текста, фото или ролика — ролики для рекламы и запусков.',
    cta: 'Попробовать видео',
    source: 'scenario_video',
  },
] as const

const features = [
  { title: 'Изображения и стили', desc: '12 режимов Style Director — от каталога до campaign hero.' },
  { title: 'Видео Studio', desc: 'Текст, картинка, видео и улучшение — в одном разделе.' },
  { title: 'Карточки товаров', desc: 'Креативы для Wildberries и Ozon с упором на CTR.' },
  { title: 'Ideogram Studio', desc: 'Текст в картинке, правки и стек вариантов для макетов.' },
  { title: 'Инфографика', desc: 'Слои, текст и экспорт для маркетплейсов и social.' },
  { title: 'Edu Shorts', desc: 'Короткие обучающие ролики из сценария — быстрее ручной сборки.' },
]

const examples = [1, 2, 3, 4, 5, 6]

const libImgs = LANDING_LIB_IMAGES

const kartinaPoints = [
  'Подскажет, какой стиль взять под задачу',
  'Поможет разобрать ошибку генерации',
  'Подскажет по балансу и статусу оплаты',
]

const LANDING_TITLE = 'KartinAI — изображения со стилями, видео и карточки для бизнеса'
const LANDING_DESCRIPTION =
  '12 стилей генерации, Video Studio, карточки для маркетплейсов. Попробуйте бесплатно — кредиты и пробный премиум после регистрации.'

const LANDING_CONTACTS = {
  email: 'kartinai@pardmind.pro',
  vk: 'https://vk.com/kartinai',
  telegram: 'https://t.me/KartinaAi_official',
} as const

export default function Landing() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [landingPlans, setLandingPlans] = useState<LandingPlanCard[]>(DEFAULT_LANDING_PLANS)
  const telegramUrl = import.meta.env.VITE_TELEGRAM_URL?.trim()
  const vkUrl = import.meta.env.VITE_VK_URL?.trim()

  useEffect(() => {
    subscriptionsApi.getPlans()
      .then(res => {
        setLandingPlans(mergeLandingPlans(res.data.free, res.data.plans ?? []))
      })
      .catch(() => {})
  }, [])

  const handleTryApp = (source: string) => {
    trackLandingCta('landing', source)
    trackEvent('guest_enter_app', { source }, { surface: 'landing' })
    navigate('/dashboard')
  }

  const handleViewExamples = (source: string) => {
    trackLandingCta('landing', source)
    const el = document.getElementById('examples')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    navigate('/library/examples')
  }

  const handlePricingCta = (plan: LandingPlanCard) => {
    if (isAuthenticated) {
      navigate('/balance')
      return
    }
    handleTryApp(`pricing_${plan.key}`)
  }

  const handleExternalOrRegister = (source: string, externalUrl?: string) => {
    trackLandingCta('landing', source)
    if (externalUrl) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer')
      return
    }
    handleTryApp(source)
  }

  useEffect(() => {
    if (!mobileMenuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    initLandingAnalytics()
    applyLandingSeo({
      page: 'landing',
      title: LANDING_TITLE,
      description: LANDING_DESCRIPTION,
      path: '/',
    })
  }, [])

  useEffect(useLandingDarkThemeEffect(), [])

  return (
    <div className="kmin bg-[#07070A] text-white min-h-screen font-sans overflow-hidden">

      <div className="kmin-glow">
        <div className="kmin-glow-top" />
        <div className="kmin-glow-bottom" />
      </div>

      <header className="kmin-nav">
        <div className="kmin-container">
          <div className="kmin-nav-inner">
            <BrandLogo variant="landing" to="/" />
            <nav className="kmin-nav-links" aria-label="Основная навигация">
              <a href="#scenarios" onClick={() => setMobileMenuOpen(false)}>Сценарии</a>
              <a href="#styles" onClick={() => setMobileMenuOpen(false)}>Стили</a>
              <a href="#features" onClick={() => setMobileMenuOpen(false)}>Возможности</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Тарифы</a>
              <Link
                to="/blog"
                onClick={() => {
                  setMobileMenuOpen(false)
                  trackLandingCta('landing', 'nav_blog')
                }}
              >
                Блог
              </Link>
              <a href="#contacts" onClick={() => setMobileMenuOpen(false)}>Контакты</a>
            </nav>
            <div className="kmin-nav-actions">
              <button
                type="button"
                className="kmin-nav-burger"
                aria-label={mobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((v) => !v)}
              >
                <span className="material-symbols-outlined">
                  {mobileMenuOpen ? 'close' : 'menu'}
                </span>
              </button>
              <button className="kmin-btn-primary kmin-nav-cta" onClick={() => handleTryApp('nav_start')}>
                Начать
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <>
            <button
              type="button"
              className="kmin-nav-mobile-backdrop"
              aria-label="Закрыть меню"
              onClick={() => setMobileMenuOpen(false)}
            />
            <nav className="kmin-nav-mobile-sheet" aria-label="Мобильное меню">
              <a href="#scenarios" onClick={() => setMobileMenuOpen(false)}>Сценарии</a>
              <a href="#styles" onClick={() => setMobileMenuOpen(false)}>Стили</a>
              <a href="#features" onClick={() => setMobileMenuOpen(false)}>Возможности</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Тарифы</a>
              <Link
                to="/blog"
                onClick={() => {
                  setMobileMenuOpen(false)
                  trackLandingCta('landing', 'nav_mobile_blog')
                }}
              >
                Блог
              </Link>
              <a href="#contacts" onClick={() => setMobileMenuOpen(false)}>Контакты</a>
              <button
                type="button"
                className="kmin-btn-primary kmin-btn-block"
                onClick={() => {
                  setMobileMenuOpen(false)
                  handleTryApp('nav_mobile_sheet')
                }}
              >
                Начать бесплатно
              </button>
            </nav>
          </>
        )}
      </header>

      {/* HERO */}
      <section className="kmin-hero">
        <div className="kmin-container">
          <div className="kmin-hero-grid">
            <div>
              <div className="kmin-badge">AI-платформа для бизнеса</div>
              <h1 className="kmin-hero-title">
                <span className="kmin-hero-accent">
                  KartinAi — нейросеть
                </span>
                <span className="block">для создания контента и рекламы</span>
              </h1>
              <p className="kmin-hero-desc kmin-hero-desc--accent">
                Создавайте изображения, видео, карточки товаров, AI-аватары и рекламные материалы в одном сервисе.
              </p>
              <div className="kmin-hero-actions">
                <button className="kmin-btn-primary kmin-btn-lg" onClick={() => handleTryApp('hero_primary')}>
                  Попробовать бесплатно
                </button>
                <button className="kmin-btn-outline kmin-btn-lg" onClick={() => handleViewExamples('hero_secondary')}>
                  Смотреть примеры
                </button>
              </div>
            </div>

            <div className="kmin-hero-preview">
              <div className="kmin-hero-preview-glow" />
              <div className="kmin-hero-preview-card">
                <div className="kmin-preview-header">
                  <div>
                    <div className="font-medium">AI Студия</div>
                    <div className="kmin-preview-sub">Создание контента</div>
                  </div>
                  <div className="kmin-preview-status">Онлайн</div>
                </div>
                <div className="kmin-preview-prompt">
                  Создай рекламный креатив для соцсетей и маркетплейсов...
                </div>
                <div className="kmin-preview-grid2">
                  <div className="kmin-preview-box" onClick={() => setPreviewUrl('/landing/burger_11.mp4')}>
                    <video
                      src="/landing/burger_11.mp4"
                      poster="/landing/burger_11.jpg"
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="kmin-preview-img"
                      title="Пример рекламного видео для e-commerce"
                    />
                  </div>
                  <div className="kmin-preview-box" onClick={() => setPreviewUrl(libImgs[1])}>
                    <img
                      src={libImgs[1]}
                      alt="Пример AI-креатива для маркетплейса"
                      className="kmin-preview-img"
                      fetchPriority="high"
                    />
                  </div>
                </div>
                <div className="kmin-preview-grid3">
                  {[2, 3, 4].map((i) => (
                    <div key={i} className="kmin-preview-box-sm" onClick={() => setPreviewUrl(libImgs[i])}>
                      <img
                        src={libImgs[i]}
                        alt={
                          i === 2
                            ? 'Пример рекламного плаката KartinAi в стиле Poster'
                            : 'Пример визуала продукта, созданного в KartinAI'
                        }
                        className="kmin-preview-img-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kmin-logos">
        <div className="kmin-container">
          <div className="kmin-logos-inner">
            <span>Wildberries</span>
            <span>Ozon</span>
            <span>Telegram</span>
            <span>VK</span>
            <span>Маркетплейсы</span>
            <span>E-commerce</span>
          </div>
        </div>
      </section>

      {/* SCENARIOS */}
      <section id="scenarios" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">С чего начать</div>
            <h3 className="kmin-section-title">Решаем главные задачи <span className="block text-white/70">контент-отдела</span></h3>
            <p className="kmin-section-desc">Выберите сценарий и попробуйте бесплатно.</p>
          </div>
          <div className="kmin-scenarios-grid">
            {scenarios.map((s) => (
              <div key={s.title} className="kmin-scenario-card">
                <h4 className="kmin-feature-title">{s.title}</h4>
                <p className="kmin-feature-desc">{s.desc}</p>
                <button
                  type="button"
                  className="kmin-btn-primary kmin-btn-block"
                  onClick={() => handleTryApp(s.source)}
                >
                  {s.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STYLES */}
      <section id="styles" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Стили</div>
            <h3 className="kmin-section-title">12 режимов — <span className="block text-white/70">результат под задачу</span></h3>
            <p className="kmin-section-desc">
              Не «ещё один AI», а библиотека стилей: каталог, luxury, lifestyle, campaign и другие.
            </p>
          </div>
          <div className="kmin-styles-grid">
            {LANDING_STYLES.map((s) => (
              <button
                key={s.title}
                type="button"
                className="kmin-style-card"
                onClick={() => setPreviewUrl(s.img)}
              >
                <img src={s.img} alt={s.title} className="kmin-style-card-img" loading="lazy" />
                <div className="kmin-style-card-body">
                  <div className="kmin-style-card-title">{s.title}</div>
                  <div className="kmin-style-card-blurb">{s.blurb}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="kmin-section-cta">
            <button className="kmin-btn-primary kmin-btn-lg" onClick={() => handleTryApp('styles_cta')}>
              Попробовать бесплатно
            </button>
          </div>
        </div>
      </section>

      {/* EXAMPLES */}
      <section id="examples" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-examples-header">
            <div>
              <div className="kmin-label">Примеры</div>
              <h3 className="kmin-section-title">Контент, <span className="block text-white/70">созданный в KartinAI</span></h3>
            </div>
            <p className="kmin-section-desc" style={{ maxWidth: 500 }}>
              Баннеры, карточки, видео и фотосессии — под ваши каналы продаж.
            </p>
          </div>
          <div className="kmin-examples-grid">
            {examples.map((item, ei) => (
              <div key={item} className="kmin-example-card">
                <div className="kmin-example-img">
                  {ei === 0 ? (
                    <video src="/landing/10.mp4" poster={libImgs[9]} autoPlay muted loop playsInline className="kmin-preview-img" title="Пример AI-видео-аватара" onClick={() => setPreviewUrl('/landing/10.mp4')} />
                  ) : ei === 1 ? (
                    <video src="/landing/lipobaze_03.mp4" poster="/landing/lipobaze_03.jpg" autoPlay muted loop playsInline className="kmin-preview-img" title="Пример рекламного AI-видео" onClick={() => setPreviewUrl('/landing/lipobaze_03.mp4')} />
                  ) : ei === 3 ? (
                    <video src="/landing/9.mp4" poster={libImgs[12]} autoPlay muted loop playsInline className="kmin-preview-img" title="Пример видео 360" onClick={() => setPreviewUrl('/landing/9.mp4')} />
                  ) : ei === 5 ? (
                    <img src="/landing/generated-fd19377.png" alt="Пример рекламной AI-фотосессии" className="kmin-preview-img" loading="lazy" onClick={() => setPreviewUrl('/landing/generated-fd19377.png')} />
                  ) : (
                    <img src={libImgs[9 + ei]} alt="Пример рекламного креатива, созданного в KartinAI" className="kmin-preview-img" loading="lazy" onClick={() => setPreviewUrl(libImgs[9 + ei])} />
                  )}
                </div>
                <div className="kmin-example-body">
                  <div className="kmin-example-meta">
                    <span>AI Generated</span>
                    <span className="text-emerald-400">PRO</span>
                  </div>
                  <h4 className="kmin-feature-title">
                    {ei === 0
                      ? 'Видео-аватар'
                      : ei === 1
                        ? 'Видео-карточка товара'
                        : ei === 2
                          ? 'Карточки товаров'
                          : ei === 3
                            ? 'Видео 360'
                            : ei === 5
                              ? 'Рекламные фотосессии'
                              : 'Рекламный креатив'}
                  </h4>
                  <p className="kmin-example-desc">
                    {ei === 0
                      ? 'Создавай и озвучивай AI-аватаров'
                      : ei === 1
                        ? 'Любое статичное изображение — в продающее видео'
                        : ei === 2
                          ? 'Улучшай продажи на маркетплейсах'
                          : ei === 3
                            ? 'Круговой обзор товара из одного фото'
                            : ei === 5
                              ? 'Портреты и fashion-съёмка для рекламы и соцсетей'
                              : 'Premium визуал для соцсетей и маркетплейсов.'}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="kmin-section-cta">
            <button className="kmin-btn-outline kmin-btn-lg" onClick={() => handleTryApp('examples_cta')}>
              Попробовать бесплатно
            </button>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Возможности</div>
            <h3 className="kmin-section-title">Инструменты <span className="block text-white/70">после регистрации</span></h3>
            <p className="kmin-section-desc">Коротко о том, что уже в продукте — без перегруза.</p>
          </div>
          <div className="kmin-features-grid kmin-features-grid--compact">
            {features.map((item) => (
              <div key={item.title} className="kmin-feature-card kmin-feature-card--compact">
                <div className="kmin-feature-body">
                  <h4 className="kmin-feature-title">{item.title}</h4>
                  <p className="kmin-feature-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="kmin-designers-link">
            Студия или фриланс?{' '}
            <Link to="/designers" onClick={() => trackLandingCta('landing', 'features_designers')}>
              Условия для дизайнеров
            </Link>
          </p>
        </div>
      </section>

      {/* KARTINA */}
      <section id="assistant" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-assistant-grid">
            <div>
              <div className="kmin-label">Kartina</div>
              <h3 className="kmin-section-title">Помощник <span className="block text-white/70">внутри сервиса</span></h3>
              <p className="kmin-section-desc" style={{ maxWidth: 440, marginBottom: 40 }}>
                Kartina подскажет по генерациям, стилям и оплате — бесплатно для пользователей сервиса.
                Отдельно есть ИИ Чат для текстов и идей.
              </p>
              <div className="kmin-usecases">
                {kartinaPoints.map(item => (
                  <div key={item} className="kmin-usecase-item">{item}</div>
                ))}
              </div>
            </div>

            <div className="kmin-chat-card">
              <div className="kmin-chat-header">
                <div>
                  <div className="font-medium">Kartina</div>
                  <div className="kmin-preview-sub">Помощь в сервисе</div>
                </div>
                <div className="kmin-chat-badge">FREE</div>
              </div>
              <div className="kmin-chat-body">
                <div className="kmin-chat-user">
                  Какой стиль взять для карточки наушников на WB?
                </div>
                <div className="kmin-chat-ai">
                  Для чистой карточки — <strong>KRT Studio</strong>. Если нужен вау-баннер запуска —
                  <strong> KRT Campaign</strong>.
                  <div className="kmin-chat-actions">
                    <button
                      type="button"
                      className="kmin-btn-primary kmin-btn-sm"
                      onClick={() => handleTryApp('kartina_try_studio')}
                    >
                      Открыть сервис
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Тарифы</div>
            <h3 className="kmin-section-title">Начните бесплатно</h3>
            <p className="kmin-section-desc">Сначала Free — масштабируйтесь, когда поток задач вырастет.</p>
          </div>
          <div className="kmin-pricing-grid">
            {landingPlans.map(plan => (
              <div key={plan.key} className={`kmin-plan-card ${plan.accent ? 'kmin-plan-card--accent' : ''}`}>
                <div className="kmin-plan-name">{plan.title}</div>
                <div className="kmin-plan-price">{plan.price}</div>
                <p className="kmin-plan-desc">{plan.desc}</p>
                <button
                  className={plan.accent ? 'kmin-btn-primary kmin-btn-block' : 'kmin-btn-outline kmin-btn-block'}
                  onClick={() => handlePricingCta(plan)}
                >
                  {isAuthenticated
                    ? 'Выбрать тариф'
                    : plan.key === 'free'
                      ? 'Попробовать бесплатно'
                      : 'Выбрать'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACTS */}
      <section id="contacts" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-contacts-card">
            <div className="kmin-contacts-glow" />
            <div className="kmin-contacts-content">
              <div className="kmin-section-header" style={{ marginBottom: 48 }}>
                <div className="kmin-label">Контакты</div>
                <h3 className="kmin-section-title">
                  На связи
                  <span className="block text-white/70">с командой KartinAI</span>
                </h3>
                <p className="kmin-section-desc">
                  Вопросы по сервису, сотрудничеству и поддержке — пишите удобным способом.
                </p>
              </div>
              <div className="kmin-contacts-grid">
                <a
                  href={`mailto:${LANDING_CONTACTS.email}`}
                  className="kmin-contact-item"
                  onClick={() => trackLandingCta('landing', 'contacts_email')}
                >
                  <span className="kmin-contact-icon material-symbols-outlined" aria-hidden="true">mail</span>
                  <div className="kmin-contact-text">
                    <span className="kmin-contact-label">Email</span>
                    <span className="kmin-contact-value">{LANDING_CONTACTS.email}</span>
                  </div>
                  <span className="kmin-contact-arrow material-symbols-outlined" aria-hidden="true">arrow_outward</span>
                </a>
                <a
                  href={LANDING_CONTACTS.vk}
                  className="kmin-contact-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackLandingCta('landing', 'contacts_vk')}
                >
                  <span className="kmin-contact-icon material-symbols-outlined" aria-hidden="true">groups</span>
                  <div className="kmin-contact-text">
                    <span className="kmin-contact-label">ВКонтакте</span>
                    <span className="kmin-contact-value">vk.com/kartinai</span>
                  </div>
                  <span className="kmin-contact-arrow material-symbols-outlined" aria-hidden="true">arrow_outward</span>
                </a>
                <a
                  href={LANDING_CONTACTS.telegram}
                  className="kmin-contact-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackLandingCta('landing', 'contacts_telegram')}
                >
                  <span className="kmin-contact-icon material-symbols-outlined" aria-hidden="true">send</span>
                  <div className="kmin-contact-text">
                    <span className="kmin-contact-label">Telegram</span>
                    <span className="kmin-contact-value">@KartinaAi_official</span>
                  </div>
                  <span className="kmin-contact-arrow material-symbols-outlined" aria-hidden="true">arrow_outward</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="kmin-footer">
        <div className="kmin-container">
          <div className="kmin-footer-inner">
            <div>
              <BrandLogo variant="landing-sm" to="/" />
              <div className="kmin-footer-sub">AI-платформа для бизнеса и контента</div>
              <div className="kmin-footer-legal">
                <div>ИП Вихарев С.В.</div>
                <div>ИНН: 745202549414</div>
                <div>ОГРНИП: 320237500261078</div>
              </div>
            </div>
            <div className="kmin-footer-links">
              <Link to="/blog" className="kmin-footer-link-btn" onClick={() => trackLandingCta('landing', 'footer_blog')}>
                Блог
              </Link>
              <Link to="/designers" className="kmin-footer-link-btn" onClick={() => trackLandingCta('landing', 'footer_designers')}>
                Дизайнерам
              </Link>
              <button
                type="button"
                className="kmin-footer-link-btn"
                onClick={() => handleExternalOrRegister('footer_telegram', telegramUrl)}
              >
                Telegram
              </button>
              <button
                type="button"
                className="kmin-footer-link-btn"
                onClick={() => handleExternalOrRegister('footer_vk', vkUrl)}
              >
                VK
              </button>
              <button
                type="button"
                className="kmin-footer-link-btn"
                onClick={() => handleTryApp('footer_support')}
              >
                Поддержка
              </button>
            </div>
          </div>
        </div>
      </footer>

      {previewUrl && (
        <div className="kmin-preview-overlay" onClick={() => setPreviewUrl(null)}>
          <button className="kmin-preview-close" onClick={() => setPreviewUrl(null)}>
            <span className="material-symbols-outlined">close</span>
          </button>
          {previewUrl.endsWith('.mp4') ? (
            <video src={previewUrl} autoPlay loop playsInline className="kmin-preview-full" controls onClick={e => e.stopPropagation()} />
          ) : (
            <img src={previewUrl} alt="Увеличенный предпросмотр контента KartinAI" className="kmin-preview-full" />
          )}
        </div>
      )}
    </div>
  )
}
